import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api, formatCurrency, formatDateTime, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumericInput } from '../components/ui/numeric-input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useStore } from '../context/StoreContext';
import {
  ShoppingCart,
  Search,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Banknote,
  Camera,
  X,
  Receipt,
  Keyboard,
  Shield,
  Printer,
  Euro,
  DollarSign,
  Calculator,
  Edit,
  HelpCircle,
  Users,
  Barcode
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Keyboard shortcuts reference
const KEYBOARD_SHORTCUTS = [
  { key: 'F1', action: 'Kërko produkt' },
  { key: 'F2', action: 'Barcode manual' },
  { key: 'F3', action: 'Kamera' },
  { key: 'F4', action: 'Paguaj saktë' },
  { key: 'F5', action: 'Garanci On/Off' },
  { key: 'F6', action: 'Pastro shportën' },
  { key: 'F7', action: 'Kesh/Kartë' },
  { key: 'F8', action: 'Historia' },
  { key: 'F9', action: '+50 LEK zbritje' },
  { key: 'F10', action: '+100 LEK zbritje' },
  { key: 'F11', action: 'Klient i ri' },
  { key: 'F12', action: 'Përfundo shitjen' },
  { key: 'Esc', action: 'Hiq zbritjen' },
];

export default function Sales() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [paid, setPaid] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  // Invoice template (loaded from store)
  const [invoiceTemplate, setInvoiceTemplate] = useState(null);
  const [warrantyTemplate, setWarrantyTemplate] = useState(null);
  const [warrantyMonths, setWarrantyMonths] = useState(12);
  const [createWarranty, setCreateWarranty] = useState(false);
  const [isManualBarcodeOpen, setIsManualBarcodeOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);

  // Edit sale modal
  const [isEditSaleOpen, setIsEditSaleOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [editPaid, setEditPaid] = useState(0);

  // New customer modal
  const [isNewCustomerOpen, setIsNewCustomerOpen] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({ name: '', phone: '', email: '', address: '' });

  // Post-sale warranty modal
  const [isPostSaleWarrantyOpen, setIsPostSaleWarrantyOpen] = useState(false);
  const [postSaleWarrantyData, setPostSaleWarrantyData] = useState({ items: [], customerId: '', customerName: '', saleId: '' });
  const [postWarrantyForm, setPostWarrantyForm] = useState({
    type: 'product',
    category: 'smartphone',
    product_id: '',
    product_name: '',
    serial_number: '',
    duration_months: 12,
    notes: '',
  });
  const [createdWarranty, setCreatedWarranty] = useState(null);

  // Store context
  const { currentStore, stores, canSwitchStores, user } = useStore();

  // Helper to get store name by id
  const getStoreName = (storeId) => {
    const store = stores?.find(s => s.id === storeId);
    return store?.name || '-';
  };

  // Show store column when viewing all stores
  const showStoreColumn = canSwitchStores && !currentStore;

  // Check if store supports warranties (Tech stores only)
  const supportsWarranties = !currentStore || !['boutique', 'luxstore', 'botique', 'luxshop'].includes(currentStore.type?.toLowerCase());

  // Warranty categories
  const WARRANTY_CATEGORIES = [
    { value: 'smartphone', label: 'Smartphone', defaultMonths: 12 },
    { value: 'laptop', label: 'Laptop', defaultMonths: 24 },
    { value: 'tablet', label: 'Tablet', defaultMonths: 12 },
    { value: 'tv', label: 'Televizor', defaultMonths: 24 },
    { value: 'ac', label: 'Kondicionier', defaultMonths: 36 },
    { value: 'appliance', label: 'Elektroshtëpiake', defaultMonths: 24 },
    { value: 'accessory', label: 'Aksesor', defaultMonths: 6 },
    { value: 'repair', label: 'Riparim', defaultMonths: 3 },
    { value: 'other', label: 'Tjetër', defaultMonths: 12 },
  ];

  // Currency payment
  const [paymentCurrency, setPaymentCurrency] = useState('ALL');
  const [foreignAmount, setForeignAmount] = useState(0);
  const [givenAmount, setGivenAmount] = useState(0);

  // Exchange rates from backend
  const [exchangeRates, setExchangeRates] = useState({ EUR: 10, USD: 9.5 });

  const createNewCustomer = async () => {
    if (!newCustomerData.name.trim()) {
      toast.error('Emri është i detyrueshëm');
      return;
    }
    try {
      const customerData = { ...newCustomerData };
      if (currentStore?.id) {
        customerData.store_id = currentStore.id;
      }
      const response = await api.post('/customers', customerData);
      const newCustomer = response.data;
      setCustomers([...customers, newCustomer]);
      setSelectedCustomer(newCustomer);
      setIsNewCustomerOpen(false);
      setNewCustomerData({ name: '', phone: '', email: '', address: '' });
      toast.success('Klienti u krijua');
    } catch (error) {
      toast.error('Gabim në krijimin e klientit');
    }
  };

  const loadExchangeRates = useCallback(async () => {
    try {
      const response = await api.get('/exchange-rates');
      setExchangeRates({
        EUR: response.data.EUR || 10,
        USD: response.data.USD || 9.5
      });
    } catch (error) {
      console.error('Error loading exchange rates:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const storeParam = currentStore?.id ? `?store_id=${currentStore.id}` : '';
      const [productsRes, customersRes, salesRes, storesRes] = await Promise.all([
        api.get(`/products${storeParam}`),
        api.get(`/customers${storeParam}`),
        api.get(`/sales${storeParam}`),
        api.get('/stores')
      ]);
      setProducts(productsRes.data);
      setCustomers(customersRes.data);
      setSales(salesRes.data);

      // Get store for invoice template
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const allStores = storesRes.data;

      // Find store for invoice template
      let store = currentStore;
      if (!store && user?.store_id) {
        store = allStores.find(s => s.id === user.store_id);
      }
      if (!store && allStores.length > 0) {
        store = allStores[0];
      }

      // Load invoice template for this store
      if (store) {
        const savedTemplate = localStorage.getItem(`invoice_template_${store.id}`);
        if (savedTemplate) {
          setInvoiceTemplate(JSON.parse(savedTemplate));
        } else {
          // Default template from store data
          setInvoiceTemplate({
            store_name: store.name || 'TECH STORE',
            address: store.address || '',
            phone: store.phone || '',
            nipt: store.nipt || '',
            logo_url: store.logo_url || '',
            header_text: 'Faturë Tatimore',
            footer_text: store.invoice_footer || 'Faleminderit për blerjen!',
            show_nipt: store.invoice_show_nipt !== false,
            show_phone: store.invoice_show_phone !== false,
            show_address: store.invoice_show_address !== false,
            primary_color: store.invoice_primary_color || '#1a1a1a',
            font_size: 'medium'
          });
        }

        // Load warranty template for this store
        const savedWarranty = localStorage.getItem(`warranty_template_${store.id}`);
        if (savedWarranty) {
          setWarrantyTemplate(JSON.parse(savedWarranty));
        } else {
          setWarrantyTemplate({
            warranty_primary_color: '#6D28D9',
            warranty_secondary_color: '#84CC16',
            warranty_font_family: 'Arial',
            warranty_show_logo: true,
            warranty_show_terms: true,
            warranty_covered_terms: 'Garancia mbulon defektet e prodhimit\nRiparimi kryhet brenda 14 ditëve pune\nZëvendësimi bëhet vetëm nëse riparimi është i pamundur',
            warranty_excluded_terms: 'Nuk mbulon dëmtimet fizike\nNuk mbulon dëmtimet nga uji\nNuk mbulon keqpërdorimin',
            warranty_footer_text: 'Ruajeni këtë kartë dhe faturën origjinale',
            store_name: store.name,
            phone: store.phone,
            logo_url: store.logo_url
          });
        }
      }
    } catch (error) {
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  }, [currentStore]);

  useEffect(() => {
    loadData();
    loadExchangeRates();
  }, [loadData, loadExchangeRates, currentStore]);

  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);

  // Start camera scanner
  const startCameraScanner = async () => {
    // Check if browser supports getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Shfletuesi juaj nuk e mbështet kamerën. Përdorni Chrome ose Firefox.');
      return;
    }

    setIsScannerOpen(true);

    // Wait for dialog to render
    setTimeout(async () => {
      try {
        // First try to get camera permission
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        stream.getTracks().forEach(track => track.stop()); // Stop the test stream

        const html5QrCode = new Html5Qrcode("camera-reader");
        html5QrCodeRef.current = html5QrCode;

        const config = {
          fps: 10,
          qrbox: { width: 250, height: 150 },
          aspectRatio: 1.777778,
          showTorchButtonIfSupported: true,
          showZoomSliderIfSupported: true
        };

        await html5QrCode.start(
          { facingMode: "environment" },
          config,
          (decodedText) => {
            handleBarcodeScanned(decodedText);
            stopCameraScanner();
          },
          (errorMessage) => {
            // Ignore scan errors - these are normal when no barcode is in view
          }
        );

        toast.success('Kamera u hap. Vendosni barkodin para kamerës.');
      } catch (err) {
        console.error("Camera error:", err);
        let errorMsg = 'Nuk mund të hapet kamera.';

        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMsg = 'Leja për kamerën u refuzua. Lejoni aksesin në cilësimet e shfletuesit.';
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMsg = 'Nuk u gjet asnjë kamerë në këtë pajisje.';
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          errorMsg = 'Kamera po përdoret nga një aplikacion tjetër.';
        } else if (err.name === 'OverconstrainedError') {
          errorMsg = 'Kamera nuk plotëson kërkesat.';
        } else if (err.name === 'NotSupportedError') {
          errorMsg = 'HTTPS kërkohet për të përdorur kamerën. Përdorni HTTPS ose localhost.';
        }

        toast.error(errorMsg);
        setIsScannerOpen(false);
      }
    }, 500);
  };

  const stopCameraScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error("Stop error:", err);
      }
    }
    setIsScannerOpen(false);
  };

  const addToCart = useCallback((product) => {
    setCart(currentCart => {
      const existingItem = currentCart.find(item => item.product_id === product.id);
      if (existingItem) {
        if (existingItem.quantity >= product.stock) {
          toast.error('Stoku i pamjaftueshëm');
          return currentCart;
        }
        return currentCart.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.price }
            : item
        );
      } else {
        if (product.stock <= 0) {
          toast.error('Produkti nuk ka stok');
          return currentCart;
        }
        return [...currentCart, {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          price: product.sale_price,
          total: product.sale_price
        }];
      }
    });
  }, []);

  const handleBarcodeScanned = useCallback(async (barcode) => {
    const cleanBarcode = barcode.trim();
    if (!cleanBarcode) return;

    try {
      const response = await api.get(`/products/barcode/${cleanBarcode}`);
      addToCart(response.data);
      toast.success(`${response.data.name} u shtua në shportë`);
    } catch (error) {
      toast.error('Produkti nuk u gjet: ' + cleanBarcode);
    }
  }, [addToCart]);

  // Handle keyboard barcode input (from USB scanner) and keyboard shortcuts
  useEffect(() => {
    let barcodeBuffer = '';
    let lastKeyTime = 0;

    const handleKeyPress = (e) => {
      // Skip if we're in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      const currentTime = new Date().getTime();

      if (currentTime - lastKeyTime > 100) {
        barcodeBuffer = '';
      }
      lastKeyTime = currentTime;

      if (e.key === 'Enter' && barcodeBuffer.length >= 3) {
        e.preventDefault();
        handleBarcodeScanned(barcodeBuffer);
        barcodeBuffer = '';
      } else if (e.key.length === 1 && !e.ctrlKey && !e.altKey) {
        if (/^[0-9a-zA-Z]$/.test(e.key)) {
          barcodeBuffer += e.key;
        }
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [handleBarcodeScanned]);

  const handleManualBarcode = () => {
    if (barcodeInput.trim()) {
      handleBarcodeScanned(barcodeInput);
      setBarcodeInput('');
      setIsManualBarcodeOpen(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(search.toLowerCase()) ||
    product.barcode.includes(search)
  );

  const updateQuantity = (productId, newQuantity) => {
    const product = products.find(p => p.id === productId);
    if (newQuantity > product.stock) {
      toast.error('Stoku i pamjaftueshëm');
      return;
    }
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(cart.map(item =>
      item.product_id === productId
        ? { ...item, quantity: newQuantity, total: newQuantity * item.price }
        : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
  const total = subtotal - discount;
  const change = Math.max(0, paid - total);

  // Keyboard shortcuts (F1-F12) - placed after total is defined
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if we're in an input field or modal is open
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key) {
        case 'F1':
          e.preventDefault();
          document.querySelector('[data-testid="search-sale-product"]')?.focus();
          toast.info('F1: Kërko produkt');
          break;
        case 'F2':
          e.preventDefault();
          setIsManualBarcodeOpen(true);
          toast.info('F2: Barcode manual');
          break;
        case 'F4':
          e.preventDefault();
          setPaid(total);
          toast.info('F4: Paguaj saktë');
          break;
        case 'F5':
          e.preventDefault();
          if (!supportsWarranties) {
            toast.info('Garancitë nuk janë të disponueshme për këtë dyqan');
            return;
          }
          if (selectedCustomer) {
            setCreateWarranty(prev => !prev);
            toast.info(createWarranty ? 'F5: Garanci OFF' : 'F5: Garanci ON');
          } else {
            toast.warning('Zgjidhni një klient për garancinë');
          }
          break;
        case 'F6':
          e.preventDefault();
          if (cart.length > 0) {
            setCart([]);
            toast.info('F6: Shporta u pastrua');
          }
          break;
        case 'F7':
          e.preventDefault();
          setPaymentMethod(prev => prev === 'cash' ? 'card' : 'cash');
          toast.info(`F7: ${paymentMethod === 'cash' ? 'Kartë' : 'Kesh'}`);
          break;
        case 'F8':
          e.preventDefault();
          setIsHistoryOpen(true);
          toast.info('F8: Historia');
          break;
        case 'F9':
          e.preventDefault();
          setDiscount(prev => prev + 50);
          toast.info('F9: +50 LEK zbritje');
          break;
        case 'F10':
          e.preventDefault();
          setDiscount(prev => prev + 100);
          toast.info('F10: +100 LEK zbritje');
          break;
        case 'F11':
          e.preventDefault();
          setIsNewCustomerOpen(true);
          toast.info('F11: Klient i ri');
          break;
        case 'Escape':
          if (discount > 0) {
            setDiscount(0);
            toast.info('Zbritja u hoq');
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [total, cart, selectedCustomer, createWarranty, paymentMethod, discount, supportsWarranties]);

  // Currency conversion
  const convertToLek = (amount, currency) => {
    if (currency === 'ALL') return amount;
    return Math.round(amount * exchangeRates[currency]);
  };

  const convertFromLek = (amount, currency) => {
    if (currency === 'ALL') return amount;
    return (amount / exchangeRates[currency]).toFixed(2);
  };

  // Handle foreign currency payment
  const handleForeignPayment = (currency) => {
    setPaymentCurrency(currency);
    if (currency !== 'ALL') {
      const foreignTotal = convertFromLek(total, currency);
      setForeignAmount(parseFloat(foreignTotal));
      setPaid(total);
    } else {
      setForeignAmount(0);
      setPaid(total);
    }
  };

  // Update paid when foreign amount changes
  const handleForeignAmountChange = (amount) => {
    setForeignAmount(amount);
    const lekAmount = convertToLek(amount, paymentCurrency);
    setPaid(lekAmount);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Shporta është bosh');
      return;
    }

    const debt = Math.max(0, total - paid);

    try {
      const saleData = {
        customer_id: selectedCustomer?.id || '',
        customer_name: selectedCustomer?.name || 'Klient i rastësishëm',
        items: cart,
        subtotal,
        discount,
        total,
        paid,
        debt,
        payment_method: paymentMethod,
        notes: `${notes}${paymentCurrency !== 'ALL' ? ' [Paguar: ' + foreignAmount + ' ' + paymentCurrency + ']' : ''}`,
        store_id: currentStore?.id || ''
      };

      const response = await api.post('/sales', saleData);
      const saleId = response.data.id;

      // Create warranties for products if enabled and supported
      if (supportsWarranties && createWarranty && selectedCustomer && warrantyMonths > 0) {
        for (const item of cart) {
          const product = products.find(p => p.id === item.product_id);
          if (product) {
            const startDate = new Date();
            const endDate = new Date();
            endDate.setMonth(endDate.getMonth() + warrantyMonths);

            const keywordMap = {
              'tv': 'tv',
              'televizor': 'tv',
              'laptop': 'laptop',
              'macbook': 'laptop',
              'notebook': 'laptop',
              'tablet': 'tablet',
              'ipad': 'tablet',
              'condicioner': 'ac',
              'kondicioner': 'ac',
              'ac': 'ac',
              'lavatrice': 'appliance',
              'frigorifer': 'appliance',
              'aksesor': 'accessory',
              'riparim': 'repair'
            };

            let detectedCategory = 'smartphone';
            const lowerName = item.product_name.toLowerCase();

            for (const [key, value] of Object.entries(keywordMap)) {
              if (lowerName.includes(key)) {
                detectedCategory = value;
                break;
              }
            }

            await api.post('/warranties', {
              type: 'product',
              category: detectedCategory,
              product_id: item.product_id,
              product_name: item.product_name,
              customer_id: selectedCustomer.id,
              customer_name: selectedCustomer.name,
              sale_id: saleId,
              start_date: startDate.toISOString(),
              end_date: endDate.toISOString(),
              duration_months: warrantyMonths,
              notes: `Garanci automatike nga shitja #${saleId.slice(0, 8)}`,
              status: 'active',
              store_id: currentStore?.id || ''
            });
          }
        }
      }

      setLastSale({
        ...response.data,
        change,
        paymentCurrency,
        foreignAmount,
        warrantyCreated: createWarranty && selectedCustomer,
        warrantyMonths
      });
      toast.success('Shitja u regjistrua me sukses');

      // If no warranty was created but customer exists and store supports warranties, offer to create one
      if (supportsWarranties && !createWarranty && selectedCustomer) {
        const cartItems = [...cart];
        setPostSaleWarrantyData({
          items: cartItems,
          customerId: selectedCustomer.id,
          customerName: selectedCustomer.name,
          saleId: saleId
        });
        // Auto-select the first product with detected category
        if (cartItems.length > 0) {
          const item = cartItems[0];
          let detectedCategory = 'smartphone';
          const keywordMap = {
            'tv': 'tv', 'televizor': 'tv',
            'laptop': 'laptop', 'macbook': 'laptop', 'notebook': 'laptop',
            'tablet': 'tablet', 'ipad': 'tablet',
            'condicioner': 'ac', 'kondicioner': 'ac', 'ac': 'ac',
            'lavatrice': 'appliance', 'frigorifer': 'appliance',
            'aksesor': 'accessory', 'riparim': 'repair'
          };
          const lower = item.product_name.toLowerCase();
          for (const [key, value] of Object.entries(keywordMap)) {
            if (lower.includes(key)) {
              detectedCategory = value;
              break;
            }
          }
          const catInfo = WARRANTY_CATEGORIES.find(c => c.value === detectedCategory);

          setPostWarrantyForm(prev => ({
            ...prev,
            product_id: item.product_id,
            product_name: item.product_name,
            category: detectedCategory,
            duration_months: catInfo?.defaultMonths || 12
          }));
        }
      }

      // Reset form
      setCart([]);
      setSelectedCustomer(null);
      setDiscount(0);
      setPaid(0);
      setNotes('');
      setCreateWarranty(false);
      setPaymentCurrency('ALL');
      setForeignAmount(0);
      setGivenAmount(0);
      setIsReceiptOpen(true);
      loadData();
    } catch (error) {
      toast.error('Gabim në regjistrimin e shitjes');
    }
  };

  const printReceipt = () => {
    // Get template settings or use defaults
    const t = invoiceTemplate || {
      store_name: 'TECH STORE',
      address: '',
      phone: '+355 69 XXX XXXX',
      nipt: 'AL123456789',
      logo_url: '',
      header_text: 'Faturë Tatimore',
      footer_text: 'Faleminderit për blerjen!',
      show_nipt: true,
      show_phone: true,
      show_address: true,
      primary_color: '#1a1a1a',
      font_size: 'medium'
    };

    const fontSizes = { small: '10px', medium: '12px', large: '14px' };
    const fontSize = fontSizes[t.font_size] || '12px';

    const receiptWindow = window.open('', '_blank', 'width=400,height=700');
    receiptWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Faturë - ${lastSale?.id?.slice(0, 8)}</title>
          <style>
            @page { margin: 0; size: 80mm auto; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', monospace; 
              padding: 10px;
              width: 80mm;
              font-size: ${fontSize};
            }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 1px dashed ${t.primary_color}; padding-bottom: 10px; }
            .header .logo { max-width: 50px; max-height: 50px; margin-bottom: 5px; }
            .header h2 { font-size: 18px; margin-bottom: 5px; color: ${t.primary_color}; }
            .info { margin-bottom: 10px; }
            .info p { margin: 3px 0; }
            .line { border-top: 1px dashed ${t.primary_color}; margin: 10px 0; }
            .items { margin-bottom: 10px; }
            .item { display: flex; justify-content: space-between; margin: 5px 0; font-size: 11px; }
            .item-name { flex: 1; }
            .item-qty { width: 30px; text-align: center; }
            .item-price { width: 70px; text-align: right; }
            .totals { margin-top: 10px; }
            .totals .row { display: flex; justify-content: space-between; margin: 3px 0; }
            .totals .total { font-size: 16px; font-weight: bold; margin-top: 5px; }
            .change-box { background: ${t.primary_color}; color: #fff; padding: 10px; text-align: center; margin: 10px 0; }
            .change-box h3 { font-size: 20px; }
            .footer { text-align: center; margin-top: 20px; font-size: 11px; }
            .warranty { background: #f0f0f0; padding: 8px; margin: 10px 0; border: 1px dashed ${t.primary_color}; text-align: center; }
          </style>
        </head>
        <body>
          <div class="header">
            ${t.logo_url ? `<img src="${t.logo_url}" class="logo" alt="Logo" />` : ''}
            <h2>${t.store_name}</h2>
            ${t.show_address && t.address ? `<p style="font-size:10px">${t.address}</p>` : ''}
            <p>${t.header_text}</p>
            ${t.show_nipt && t.nipt ? `<p style="font-size:10px">NIPT: ${t.nipt}</p>` : ''}
          </div>
          
          <div class="info">
            <p><strong>Nr:</strong> ${lastSale?.id?.slice(0, 8).toUpperCase()}</p>
            <p><strong>Data:</strong> ${formatDateTime(lastSale?.created_at)}</p>
            <p><strong>Klienti:</strong> ${lastSale?.customer_name}</p>
          </div>
          
          <div class="line"></div>
          
          <div class="items">
            ${lastSale?.items?.map(item => `
              <div class="item">
                <span class="item-name">${item.product_name}</span>
                <span class="item-qty">x${item.quantity}</span>
                <span class="item-price">${formatCurrency(item.total)}</span>
              </div>
            `).join('')}
          </div>
          
          <div class="line"></div>
          
          <div class="totals">
            <div class="row">
              <span>Nëntotal:</span>
              <span>${formatCurrency(lastSale?.subtotal)}</span>
            </div>
            ${lastSale?.discount > 0 ? `
              <div class="row">
                <span>Zbritje:</span>
                <span>-${formatCurrency(lastSale?.discount)}</span>
              </div>
            ` : ''}
            <div class="row total">
              <span>TOTAL:</span>
              <span>${formatCurrency(lastSale?.total)}</span>
            </div>
            ${lastSale?.paymentCurrency !== 'ALL' ? `
              <div class="row">
                <span>Paguar (${lastSale?.paymentCurrency}):</span>
                <span>${lastSale?.foreignAmount} ${lastSale?.paymentCurrency}</span>
              </div>
            ` : ''}
            <div class="row">
              <span>Paguar:</span>
              <span>${formatCurrency(lastSale?.paid)}</span>
            </div>
          </div>
          
          ${lastSale?.change > 0 ? `
            <div class="change-box">
              <p>KUSUR</p>
              <h3>${formatCurrency(lastSale?.change)}</h3>
            </div>
          ` : ''}
          
          ${lastSale?.debt > 0 ? `
            <div class="change-box" style="background:#c00;">
              <p>BORXH</p>
              <h3>${formatCurrency(lastSale?.debt)}</h3>
            </div>
          ` : ''}
          
          ${lastSale?.warrantyCreated ? `
            <div class="warranty">
              <p> <strong>GARANCI: ${lastSale?.warrantyMonths} MUAJ</strong></p>
              <p style="font-size:10px;">Ruani faturën!</p>
            </div>
          ` : ''}
          
          <div class="footer">
            <div class="line"></div>
            <p>${t.footer_text}</p>
            ${t.show_phone && t.phone ? `<p>${t.phone}</p>` : ''}
          </div>
          
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    receiptWindow.document.close();
  };

  const printWarrantyCard = () => {
    // 1. Get Global Settings from currentStore directly (Single Source of Truth)
    const storeSettings = currentStore || {};

    const settings = {
      primary_color: storeSettings.warranty_primary_color || '#E52B50', // Default matches Klientet.jsx default
      font_family: storeSettings.warranty_font_family || 'Arial',
      show_logo: storeSettings.warranty_show_logo !== false,
      show_terms: storeSettings.warranty_show_terms !== false,
      footer_text: storeSettings.warranty_footer_text || 'Ruajeni këtë kartë dhe faturën origjinale',
      covered_terms: storeSettings.warranty_covered_terms || '',
      excluded_terms: storeSettings.warranty_excluded_terms || '',
      logo_url: storeSettings.logo_url || '',
      store_name: storeSettings.name || 'MIRI SHOP', // Matches Klientet.jsx default
      phone: storeSettings.phone || ''
    };

    // 2. Detect Category from Last Sale Items
    // Use the first item to determine the category for the warranty card
    // If mixed items exist, this prioritizes the first distinct category found
    let detectedCategory = 'other';
    const items = lastSale?.items || [];

    if (items.length > 0) {
      const item = items[0];
      const lowerName = item.product_name.toLowerCase();

      const keywordMap = {
        'smartphone': ['iphone', 'samsung', 'xiaomi', 'redmi', 'pixel', 'huawei', 'galaxy', 'smartphone', 'celular'],
        'laptop': ['laptop', 'macbook', 'notebook', 'thinkpad', 'dell', 'hp', 'lenovo', 'asus'],
        'tablet': ['tablet', 'ipad', 'tab'],
        'tv': ['tv', 'televizor', 'television', 'smart tv', 'android tv'],
        'ac': ['condicioner', 'kondicioner', 'ac', 'air conditioner', 'klima'],
        'appliance': ['lavatrice', 'frigorifer', 'lavastovilje', 'furre', 'sobe'],
        'accessory': ['aksesor', 'cover', 'case', 'xham', 'karikues', 'kabello', 'kufje', 'airpods'],
        'repair': ['riparim', 'servis', 'ekran', 'bateri']
      };

      // Check against keyword map
      for (const [cat, keywords] of Object.entries(keywordMap)) {
        if (keywords.some(k => lowerName.includes(k))) {
          detectedCategory = cat;
          break;
        }
      }
    }

    // 3. Find Specific Template for Category
    // warranty_templates comes from the store object (saved by Klientet.jsx)
    const templates = storeSettings.warranty_templates || [];
    const template = templates.find(t => t.category === detectedCategory);

    // 4. Resolve Terms (Default vs Template vs Global Store Settings)
    // Logic: Template Specific > Global Store Settings > Hardcoded Defaults
    const parseTerms = (str) => str ? str.split('\n').filter(t => t.trim()) : [];

    // Hardcoded defaults (Fallback if nothing else exists)
    const DEFAULT_TERMS = {
      covered: ['Difektet e fabrikës', 'Probleme me softuerin', 'Probleme me baterinë (nëse < 80%)'],
      excluded: ['Dëmtimet fizike (thyerje)', 'Kontakt me lëngje', 'Keqpërdorimi i pajisjes']
    };

    // Helper to get category label
    const categoryLabels = {
      'smartphone': 'Smartphone',
      'laptop': 'Laptop',
      'tablet': 'Tablet',
      'tv': 'Televizor',
      'ac': 'Kondicioner',
      'appliance': 'Elektroshtëpiake',
      'accessory': 'Aksesor',
      'repair': 'Riparim',
      'other': 'Produkt'
    };
    const productTypeLabel = categoryLabels[detectedCategory] || 'Produkt';

    const covered = template?.covered_terms
      ? parseTerms(template.covered_terms)
      : (parseTerms(settings.covered_terms).length > 0 ? parseTerms(settings.covered_terms) : DEFAULT_TERMS.covered);

    const excluded = template?.excluded_terms
      ? parseTerms(template.excluded_terms)
      : (parseTerms(settings.excluded_terms).length > 0 ? parseTerms(settings.excluded_terms) : DEFAULT_TERMS.excluded);

    // Use template specific logo/store name if they exist, otherwise global
    const logoUrl = (settings.show_logo && (template?.logo_url || settings.logo_url)) || '';
    const storeName = template?.store_name || settings.store_name;

    const warrantyWindow = window.open('', '_blank');
    warrantyWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kartë Garancie - ${lastSale?.customer_name || 'Klient'}</title>
          <style>
            @page { margin: 10mm; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: ${settings.font_family}, sans-serif; padding: 20px; background: #f5f5f5; }
            .card {
              background: white;
              border: 4px solid ${settings.primary_color};
              border-radius: 16px;
              padding: 30px;
              max-width: 450px;
              margin: auto;
              box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            }
            .header {
              text-align: center;
              border-bottom: 2px solid ${settings.primary_color};
              padding-bottom: 20px;
              margin-bottom: 20px;
            }
            .header .logo { max-width: 60px; max-height: 60px; margin-bottom: 10px; }
            .header h1 { color: ${settings.primary_color}; font-size: 28px; margin-bottom: 5px; }
            .header .store-name { color: #666; font-size: 14px; }
            .product-type { 
              font-size: 14px; 
              font-weight: 600; 
              text-transform: uppercase; 
              color: #333; 
              margin-top: 5px; 
            }
            .info { margin: 20px 0; }
            .info-row { 
              display: flex; 
              justify-content: space-between; 
              padding: 12px 0;
              border-bottom: 1px solid #eee;
              font-size: 14px;
            }
            .info-row span:first-child { color: #666; }
            .info-row span:last-child { font-weight: bold; color: #333; }
            .validity {
              background: ${settings.primary_color};
              color: white;
              padding: 25px;
              border-radius: 12px;
              text-align: center;
              margin: 25px 0;
            }
            .validity h3 { font-size: 28px; margin-bottom: 8px; }
            .validity p { opacity: 0.9; }
            .terms { 
              font-size: 12px; 
              margin-top: 20px; 
              padding: 20px; 
              background: #f9f9f9; 
              border-radius: 10px; 
            }
            .terms h4 { color: ${settings.primary_color}; margin-bottom: 10px; }
            .terms .excluded h4 { color: #dc2626; }
            .terms ul { margin-left: 20px; margin-bottom: 15px; }
            .terms li { margin: 6px 0; color: #555; }
            .footer { 
              text-align: center; 
              margin-top: 25px; 
              padding-top: 20px;
              border-top: 2px dashed ${settings.primary_color};
              color: #666;
              font-size: 13px;
            }
            .footer p { margin: 5px 0; }
            .footer .phone { color: ${settings.primary_color}; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="header">
              ${logoUrl ? `<img src="${logoUrl}" class="logo" alt="Logo" />` : ''}
              <h1>KARTË GARANCIE</h1>
              <p class="store-name">${storeName}</p>
              <div class="product-type">FLETË GARANCIE - ${productTypeLabel}</div>
            </div>
            
            <div class="info">
              <div class="info-row">
                <span>Nr. Garancie:</span>
                <span>${lastSale?.id?.slice(0, 8).toUpperCase()}</span>
              </div>
              <div class="info-row">
                <span>Klienti:</span>
                <span>${lastSale?.customer_name}</span>
              </div>
              <div class="info-row">
                <span>Data Blerjes:</span>
                <span>${formatDate(lastSale?.created_at)}</span>
              </div>
              ${lastSale?.items?.map(item => `
                <div class="info-row">
                  <span>Produkti:</span>
                  <span>${item.product_name}</span>
                </div>
              `).join('')}
            </div>
            
            <div class="validity">
              <h3>${lastSale?.warrantyMonths || warrantyMonths} MUAJ GARANCI</h3>
              <p>Vlefshmëria: deri më ${formatDate(new Date(new Date().setMonth(new Date().getMonth() + (lastSale?.warrantyMonths || warrantyMonths))).toISOString())}</p>
            </div>
            
            ${settings.show_terms ? `
              <div class="terms">
                <h4>Kushtet që mbulohen:</h4>
                <ul>
                  ${covered.map(t => `<li>${t}</li>`).join('')}
                </ul>
                <div class="excluded">
                  <h4>Kushtet që NUK mbulohen:</h4>
                  <ul>
                    ${excluded.map(t => `<li>${t}</li>`).join('')}
                  </ul>
                </div>
              </div>
            ` : ''}
            
            <div class="footer">
              <p>${settings.footer_text}</p>
              ${settings.phone ? `<p class="phone">Tel: ${settings.phone}</p>` : ''}
            </div>
          </div>
          
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    warrantyWindow.document.close();
  };

  // Print receipt from history (reprint)
  const printHistoryReceipt = (sale) => {
    // Get template settings or use defaults
    const t = invoiceTemplate || {
      store_name: 'TECH STORE',
      address: '',
      phone: '+355 69 XXX XXXX',
      nipt: 'AL123456789',
      logo_url: '',
      header_text: 'Faturë Tatimore',
      footer_text: 'Faleminderit për blerjen!',
      show_nipt: true,
      show_phone: true,
      show_address: true,
      primary_color: '#1a1a1a',
      font_size: 'medium'
    };

    const fontSizes = { small: '10px', medium: '12px', large: '14px' };
    const fontSize = fontSizes[t.font_size] || '12px';

    const receiptWindow = window.open('', '_blank', 'width=400,height=700');
    receiptWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Faturë - ${sale?.id?.slice(0, 8)}</title>
          <style>
            @page { margin: 0; size: 80mm auto; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', monospace; 
              padding: 10px;
              width: 80mm;
              font-size: ${fontSize};
            }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 1px dashed ${t.primary_color}; padding-bottom: 10px; }
            .header .logo { max-width: 50px; max-height: 50px; margin-bottom: 5px; }
            .header h2 { font-size: 18px; margin-bottom: 5px; color: ${t.primary_color}; }
            .info { margin-bottom: 10px; }
            .info p { margin: 3px 0; }
            .line { border-top: 1px dashed ${t.primary_color}; margin: 10px 0; }
            .items { margin-bottom: 10px; }
            .item { display: flex; justify-content: space-between; margin: 5px 0; font-size: 11px; }
            .totals { margin-top: 10px; }
            .totals .row { display: flex; justify-content: space-between; margin: 3px 0; }
            .totals .total { font-size: 16px; font-weight: bold; margin-top: 5px; }
            .reprint { text-align: center; margin-top: 10px; font-size: 10px; color: #666; border-top: 1px dashed ${t.primary_color}; padding-top: 5px; }
            .footer { text-align: center; margin-top: 15px; font-size: 11px; }
          </style>
        </head>
        <body>
          <div class="header">
            ${t.logo_url ? `<img src="${t.logo_url}" class="logo" alt="Logo" />` : ''}
            <h2>${t.store_name}</h2>
            ${t.show_address && t.address ? `<p style="font-size:10px">${t.address}</p>` : ''}
            <p>${t.header_text}</p>
            ${t.show_nipt && t.nipt ? `<p style="font-size:10px">NIPT: ${t.nipt}</p>` : ''}
          </div>
          
          <div class="info">
            <p><strong>Nr:</strong> ${sale?.id?.slice(0, 8).toUpperCase()}</p>
            <p><strong>Data:</strong> ${formatDateTime(sale?.created_at)}</p>
            <p><strong>Klienti:</strong> ${sale?.customer_name}</p>
          </div>
          
          <div class="line"></div>
          
          <div class="items">
            ${sale?.items?.map(item => `
              <div class="item">
                <span>${item.product_name}</span>
                <span>x${item.quantity} = ${formatCurrency(item.total)}</span>
              </div>
            `).join('')}
          </div>
          
          <div class="line"></div>
          
          <div class="totals">
            <div class="row">
              <span>Nëntotal:</span>
              <span>${formatCurrency(sale?.subtotal)}</span>
            </div>
            ${sale?.discount > 0 ? `
              <div class="row">
                <span>Zbritje:</span>
                <span>-${formatCurrency(sale?.discount)}</span>
              </div>
            ` : ''}
            <div class="row total">
              <span>TOTAL:</span>
              <span>${formatCurrency(sale?.total)}</span>
            </div>
            <div class="row">
              <span>Paguar:</span>
              <span>${formatCurrency(sale?.paid)}</span>
            </div>
            ${sale?.debt > 0 ? `
              <div class="row" style="color: #c00;">
                <span>Borxh:</span>
                <span>${formatCurrency(sale?.debt)}</span>
              </div>
            ` : ''}
          </div>
          
          <div class="reprint">
            ** RI-PRINTIM **
          </div>
          
          <div class="footer">
            <div class="line"></div>
            <p>${t.footer_text}</p>
            ${t.show_phone && t.phone ? `<p>${t.phone}</p>` : ''}
          </div>
          
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    receiptWindow.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-slate-100/50 p-2 min-h-0" style={{ height: 'calc(100vh - 80px)' }} data-testid="sales-page">
      {/* Help/Shortcuts Dialog */}
      <Dialog open={isHelpOpen} onOpenChange={setIsHelpOpen}>
        <DialogContent className="max-w-2xl bg-slate-50 border-0 rounded-3xl p-0 overflow-hidden shadow-2xl">
          <div className="bg-white p-6 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-2.5 rounded-xl">
                <Keyboard className="w-6 h-6 text-slate-700" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-slate-900">Shkurtoret e Tastierës</DialogTitle>
                <DialogDescription className="text-slate-500 font-medium mt-0.5">
                  Përdorni këto shkurtore për veprime të shpejta
                </DialogDescription>
              </div>
            </div>
            {/* Close button is handled by Dialog primitives usually, but we can add one if needed, though X is default */}
          </div>

          <div className="p-6 bg-slate-50/50">
            <div className="grid grid-cols-2 gap-4">
              {KEYBOARD_SHORTCUTS.map((shortcut, idx) => (
                <div key={idx} className="bg-white p-3 rounded-xl border border-slate-200/60 shadow-sm flex items-center gap-4 group hover:border-primary/20 hover:shadow-md transition-all">
                  <span className="flex items-center justify-center w-12 h-9 bg-slate-100 font-mono font-bold text-slate-700 rounded-lg text-sm border-b-2 border-slate-200 group-hover:bg-primary group-hover:text-white group-hover:border-primary/50 transition-colors">
                    {shortcut.key}
                  </span>
                  <span className="font-semibold text-slate-600 group-hover:text-slate-900">
                    {shortcut.action}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-4 border-t border-slate-100 flex justify-end">
            <Button onClick={() => setIsHelpOpen(false)} variant="outline" className="px-8 font-bold rounded-xl h-11 border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900">
              Mbyll
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0 relative">

        {/* LEFT PANEL: PRODUCTS */}
        <div className="flex flex-col h-full gap-4 w-full lg:w-8/12">
          {/* Top Bar */}
          <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-200 flex justify-between gap-3 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Kërko (F1)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 h-12 text-lg bg-slate-50 border-slate-200 focus:bg-white focus:ring-2 focus:ring-primary/20 rounded-xl"
                data-testid="search-sale-product"
                autoFocus
              />
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" size="lg" className="h-12 w-12 p-0 rounded-xl border-slate-200 hover:border-primary hover:text-primary hover:bg-primary/5" onClick={() => setIsManualBarcodeOpen(true)} title="Barcode (F2)">
                <Barcode className="w-5 h-5" />
              </Button>
              <Button variant="outline" size="lg" className="h-12 w-12 p-0 rounded-xl border-slate-200 hover:border-primary hover:text-primary hover:bg-primary/5" onClick={startCameraScanner} title="Scan (F3)">
                <Camera className="w-5 h-5" />
              </Button>
              <Button variant="outline" size="lg" className="h-12 w-12 p-0 rounded-xl border-slate-200 hover:border-primary hover:text-primary hover:bg-primary/5" onClick={() => setIsHistoryOpen(true)} title="Historia (F8)">
                <Receipt className="w-5 h-5" />
              </Button>
              <Button variant="outline" size="lg" className="h-12 w-12 p-0 rounded-xl border-slate-200 hover:border-primary hover:text-primary hover:bg-primary/5" onClick={() => setIsHelpOpen(true)} title="Ndihmë (Shortcuts)">
                <HelpCircle className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-4 custom-scrollbar">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
              {filteredProducts.map((product) => (
                <button
                  key={product.id}
                  onClick={() => addToCart(product)}
                  disabled={product.stock <= 0}
                  className={`
                    group relative flex flex-col p-3 rounded-xl border transition-all duration-200 text-left h-full
                    ${product.stock <= 0
                      ? 'bg-slate-50 border-slate-100 opacity-60 cursor-not-allowed'
                      : 'bg-white border-slate-200 hover:border-primary hover:shadow-md active:scale-95'
                    }
                  `}
                >
                  <div className="flex-1 min-h-[40px]">
                    <h3 className="font-semibold text-slate-800 text-sm leading-snug line-clamp-3 mb-2">{product.name}</h3>
                  </div>
                  <div className="mt-auto pt-2 border-t border-slate-100 flex items-end justify-between">
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Çmimi</p>
                      <p className="text-base font-bold text-primary">{formatCurrency(product.sale_price).replace('ALL', '')}</p>
                    </div>
                    {product.stock <= product.min_stock && (
                      <span className="flex h-2 w-2 rounded-full bg-orange-500" title="Stok i ulët"></span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Floating Cart Button (Only on Mobile) */}
        {!showMobileCart && (
          <div className="lg:hidden fixed bottom-6 left-4 right-4 z-50">
            <Button
              onClick={() => setShowMobileCart(true)}
              className="w-full h-16 rounded-2xl bg-slate-900 text-white shadow-2xl flex items-center justify-between px-6 hover:bg-slate-800 transition-all border border-slate-700"
            >
              <div className="flex flex-col items-start">
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{cart.reduce((a, c) => a + c.quantity, 0)} artikuj</span>
                <span className="text-xl font-bold font-mono">{formatCurrency(total)}</span>
              </div>
              <div className="flex items-center font-bold text-primary">
                SHKO TE SHTITJA <ShoppingCart className="ml-2 w-5 h-5" />
              </div>
            </Button>
          </div>
        )}

        {/* RIGHT PANEL: CART & CHECKOUT */}
        <div className={`${showMobileCart ? 'fixed inset-0 z-50 flex w-full h-full' : 'hidden'} lg:flex lg:static lg:w-4/12 flex-col bg-slate-900 lg:rounded-2xl shadow-2xl overflow-hidden`}>

          {/* 1. TOP SECTION: Customer & Big Total */}
          <div className="bg-slate-800 p-4 border-b-2 border-slate-700 shrink-0">
            {/* Mobile Header with Close Button */}
            <div className="lg:hidden flex items-center justify-between mb-4 pb-4 border-b border-slate-700/50">
              <span className="font-black text-xl text-white flex items-center gap-2">
                <ShoppingCart className="w-6 h-6 text-primary" />
                SHPORTA
              </span>
              <Button variant="ghost" size="icon" onClick={() => setShowMobileCart(false)} className="text-slate-400 hover:text-white hover:bg-slate-700 rounded-xl">
                <X className="w-6 h-6" />
              </Button>
            </div>
            {/* Customer Select */}
            <div className="flex gap-2 mb-3">
              <Select
                value={selectedCustomer?.id || 'none'}
                onValueChange={(value) => {
                  if (value === 'none') { setSelectedCustomer(null); setCreateWarranty(false); }
                  else if (value === 'new') { setIsNewCustomerOpen(true); }
                  else { setSelectedCustomer(customers.find(c => c.id === value)); }
                }}
              >
                <SelectTrigger className="flex-1 h-12 bg-slate-700 border-slate-600 text-white focus:ring-primary/20 rounded-lg hover:bg-slate-600 transition-colors">
                  <div className="flex items-center font-medium">
                    <Users className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Klient i rastësishëm" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Klient i rastësishëm</SelectItem>
                  <SelectItem value="new" className="text-primary font-medium">+ Klient i ri</SelectItem>
                  {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
              {(supportsWarranties && selectedCustomer) && (
                <Button
                  variant={createWarranty ? "default" : "outline"}
                  size="icon"
                  className={`h-12 w-12 shrink-0 rounded-lg ${createWarranty ? 'bg-indigo-500 hover:bg-indigo-600' : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'}`}
                  onClick={() => setCreateWarranty(!createWarranty)}
                  title="Krijo Garanci"
                >
                  <Shield className="w-5 h-5" />
                </Button>
              )}
            </div>

            {/* BIG TOTAL DISPLAY */}
            <div className="bg-gradient-to-br from-primary to-primary/90 rounded-lg p-5 text-center shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 p-2 opacity-5">
                <Banknote className="w-32 h-32 text-white" />
              </div>
              <p className="text-xs font-bold text-primary-foreground/70 uppercase tracking-widest mb-1">TOTAL</p>
              <div className="text-5xl sm:text-6xl font-black text-white tracking-tight z-10 relative drop-shadow-lg">
                {formatCurrency(total).replace('ALL', '').trim()}
                <span className="text-xl align-top text-white/80 font-bold ml-2">LEK</span>
              </div>
              {cart.length > 0 && (
                <div className="mt-3 inline-flex items-center px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-bold text-white">
                  <ShoppingCart className="w-4 h-4 mr-1.5" />
                  {cart.reduce((acc, item) => acc + item.quantity, 0)} artikuj
                </div>
              )}
            </div>


          </div>

          {/* 2. MIDDLE SECTION: Scrollable Items List */}
          <div className="flex-1 overflow-y-auto p-0 bg-slate-900">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <ShoppingCart className="w-20 h-20 mb-4 stroke-1 text-slate-600" />
                <p className="font-bold text-lg text-slate-400">Shporta është bosh</p>
                <p className="text-sm text-slate-500">Skanoni ose zgjidhni produkte</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {cart.map((item) => (
                  <div key={item.product_id} className="p-4 hover:bg-slate-800 transition-colors flex gap-3 group">
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-white text-base leading-tight">{item.product_name}</span>
                        <span className="font-bold text-primary font-mono text-lg ml-3">
                          {formatCurrency(item.total)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-400 font-mono">{formatCurrency(item.price)} × {item.quantity}</span>

                        {/* Quantity Controls */}
                        <div className="flex items-center bg-slate-700 border border-slate-600 rounded-lg shadow-sm h-9">
                          <button className="px-3 h-full hover:bg-rose-500 hover:text-white rounded-l-lg transition-colors text-slate-300" onClick={() => updateQuantity(item.product_id, item.quantity - 1)}>
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-10 text-center text-sm font-bold text-white">{item.quantity}</span>
                          <button className="px-3 h-full hover:bg-emerald-500 hover:text-white rounded-r-lg transition-colors text-slate-300" onClick={() => updateQuantity(item.product_id, item.quantity + 1)}>
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 3. BOTTOM SECTION: Payment Controls (Fixed) */}
          <div className="bg-slate-800 border-t-2 border-slate-700 p-4 space-y-3">

            {/* Warranty Months (if active) */}
            {createWarranty && selectedCustomer && (
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex gap-1 flex-1">
                  {[3, 6, 12, 24].map(m => (
                    <Button
                      key={m}
                      variant={warrantyMonths === m ? 'default' : 'outline'}
                      size="sm"
                      className={`flex-1 h-8 text-xs rounded-lg ${
                        warrantyMonths === m
                          ? 'text-white font-semibold'
                          : 'text-slate-300 border-slate-600 hover:text-white hover:bg-slate-700'
                      }`}
                      onClick={() => setWarrantyMonths(m)}
                    >
                      {m}m
                    </Button>
                  ))}
                </div>
                <div className="w-16 flex items-center bg-slate-700 border border-slate-600 rounded-lg px-2 py-0.5 group focus-within:ring-2 focus-within:ring-emerald-500/50 transition-all">
                  <NumericInput
                    value={warrantyMonths || ''}
                    onChange={(e) => setWarrantyMonths(parseInt(e.target.value) || 0)}
                    min={0}
                    className="h-7 text-center font-mono font-bold text-white bg-transparent border-0 p-0 focus-visible:ring-0 placeholder:text-slate-500 text-xs"
                    placeholder="Custom"
                  />
                  <span className="text-[10px] font-bold text-slate-400 ml-0.5">m</span>
                </div>
              </div>
            )}



            {/* Discount & Subtotal - Restored Here */}
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Subtotal</span>
                <span className="font-mono font-bold text-white text-sm">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 flex items-center justify-between group focus-within:ring-2 focus-within:ring-amber-500/50 transition-all">
                <label
                  className="text-xs font-bold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-amber-400 transition-colors"
                  onClick={() => setDiscount(0)}
                >
                  Zbritje
                </label>
                <div className="w-20">
                  <NumericInput
                    value={discount || ''}
                    onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                    className="h-6 text-right font-mono font-bold text-white bg-transparent border-0 p-0 focus-visible:ring-0 placeholder:text-slate-500"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Payment Method Toggle */}
            <div className="grid grid-cols-2 gap-2">
              <button
                className={`flex items-center justify-center gap-2 h-14 rounded-lg font-bold text-base transition-all ${paymentMethod === 'cash'
                  ? 'bg-emerald-500 text-white shadow-lg scale-105'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600 border border-slate-600'
                  }`}
                onClick={() => setPaymentMethod('cash')}
              >
                <Banknote className="w-6 h-6" />
                <span className="tracking-wide">KESH</span>
              </button>
              <button
                className={`flex items-center justify-center gap-2 h-14 rounded-lg font-bold text-base transition-all ${paymentMethod === 'card'
                  ? 'bg-blue-500 text-white shadow-lg scale-105'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600 border border-slate-600'
                  }`}
                onClick={() => setPaymentMethod('card')}
              >
                <CreditCard className="w-6 h-6" />
                <span className="tracking-wide">KARTË</span>
              </button>
            </div>

            {/* Payment Input Row */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <NumericInput
                  value={paid || ''}
                  onChange={(e) => setPaid(parseFloat(e.target.value) || 0)}
                  className="h-16 pl-4 pr-16 text-3xl font-bold font-mono text-white bg-slate-700 border-2 border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="0"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400">LEK</span>
              </div>
              <Button
                variant="outline"
                className="h-16 px-5 rounded-lg bg-slate-700 border-2 border-slate-600 hover:bg-emerald-500 hover:border-emerald-500 text-white hover:text-white font-bold text-sm transition-all"
                onClick={() => setPaid(total)}
              >
                SAKTË
              </Button>
            </div>

            {/* Info Badges (Change/Debt/Foreign) */}
            <div className="flex flex-col gap-2">
              {paid > 0 && (
                <div className={`p-3 rounded-lg text-center font-bold text-base ${change > 0 ? 'bg-emerald-500 text-white' :
                  (paid < total ? 'bg-rose-500 text-white' : 'bg-slate-700 text-white')
                  }`}>
                  {change > 0 ? `KUSUR: ${formatCurrency(change)}` : (paid < total ? `BORXH: ${formatCurrency(total - paid)}` : 'SHUMA E SAKTË')}
                </div>
              )}

              {(paymentCurrency !== 'ALL' || foreignAmount > 0) && (
                <div className="bg-amber-900/50 text-amber-200 p-2 rounded-lg text-center text-xs font-medium border border-amber-700">
                  {paymentCurrency}: {foreignAmount} | 1 {paymentCurrency} = {exchangeRates[paymentCurrency]} L
                </div>
              )}
            </div>

            {/* FINISH BUTTON */}
            <Button
              className="w-full h-16 text-xl font-black rounded-lg bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-2xl hover:shadow-primary/50 active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleCheckout}
              disabled={cart.length === 0}
            >
              PËRFUNDO SHITJEN
            </Button>

          </div>
        </div>
      </div>
      {/* Manual Barcode Modal */}
      <Dialog open={isManualBarcodeOpen} onOpenChange={setIsManualBarcodeOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Shkruaj Barcode</DialogTitle>
            <DialogDescription>Shkruaj kodin e barcode-it manualisht</DialogDescription>
          </DialogHeader>
          <Input
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleManualBarcode()}
            placeholder="Shkruaj barcode..."
            className="font-mono text-lg"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManualBarcodeOpen(false)}>Anulo</Button>
            <Button onClick={handleManualBarcode}>Shto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Camera Scanner Modal */}
      <Dialog open={isScannerOpen} onOpenChange={(open) => { if (!open) stopCameraScanner(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Skano Barcode me Kamerë
            </DialogTitle>
            <DialogDescription>
              Vendosni barkodin brenda kornizës së kuqe
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div id="camera-reader" className="w-full rounded-lg overflow-hidden" style={{ minHeight: '300px' }}></div>
            <div className="text-sm text-slate-500 text-center space-y-1">
              <p>Sigurohuni që barkodi të jetë i qartë dhe i ndriçuar mirë</p>
              <p className="text-xs">Nëse kamera nuk hapet, kontrolloni lejet në cilësimet e shfletuesit</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={stopCameraScanner}>Mbyll</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sales History Modal */}
      <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Historia e Shitjeve</DialogTitle>
            <DialogDescription>Shitjet e fundit - mund të modifikoni ose fshini</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto min-h-0">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  <TableHead>Data</TableHead>
                  {showStoreColumn && <TableHead>Dyqani</TableHead>}
                  <TableHead>Klienti</TableHead>
                  <TableHead>Artikuj</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Paguar</TableHead>
                  <TableHead className="text-right">Borxh</TableHead>
                  <TableHead className="text-right">Veprime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sales
                  .filter(sale => !user || user.role !== 'seller' || new Date(sale.created_at).toDateString() === new Date().toDateString())
                  .slice(0, 30)
                  .map((sale) => (
                    <TableRow key={sale.id}>
                      <TableCell className="font-mono text-xs">{formatDateTime(sale.created_at)}</TableCell>
                      {showStoreColumn && (
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {getStoreName(sale.store_id)}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell className="text-sm">{sale.customer_name}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">
                        {sale.items?.map(i => `${i.product_name} x${i.quantity}`).join(', ')}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(sale.total)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(sale.paid)}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-rose-600">
                        {sale.debt > 0 ? formatCurrency(sale.debt) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => printHistoryReceipt(sale)}
                            title="Printo Faturën"
                          >
                            <Printer className="w-4 h-4 text-green-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingSale(sale);
                              setEditPaid(sale.paid);
                              setIsEditSaleOpen(true);
                            }}
                            title="Modifiko"
                          >
                            <Edit className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              if (window.confirm('Jeni të sigurt që doni të fshini këtë shitje? Stoku do të rikthehet.')) {
                                try {
                                  await api.delete(`/sales/${sale.id}`);
                                  toast.success('Shitja u fshi dhe stoku u rikthye');
                                  loadData();
                                } catch (e) {
                                  toast.error('Gabim në fshirje');
                                }
                              }
                            }}
                            title="Fshi"
                          >
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Modal */}
      <Dialog open={isEditSaleOpen} onOpenChange={setIsEditSaleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifiko Shitjen</DialogTitle>
            <DialogDescription>Fatura #{editingSale?.id?.slice(0, 8)}</DialogDescription>
          </DialogHeader>
          {editingSale && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">Klienti:</p>
                <p className="font-semibold">{editingSale.customer_name}</p>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600 mb-2">Artikujt:</p>
                {editingSale.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.product_name} x{item.quantity}</span>
                    <span className="font-mono">{formatCurrency(item.total)}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg">
                <span className="font-medium">Total:</span>
                <span className="font-mono font-bold text-lg">{formatCurrency(editingSale.total)}</span>
              </div>

              <div>
                <Label>Shuma e Paguar (LEK)</Label>
                <NumericInput
                  value={editPaid}
                  onChange={(e) => setEditPaid(parseFloat(e.target.value) || 0)}
                  className="font-mono text-lg"
                />
              </div>

              {editPaid < editingSale.total && (
                <div className="bg-rose-100 p-3 rounded-lg text-center">
                  <p className="text-sm text-rose-800">Borxh i ri:</p>
                  <p className="font-mono font-bold text-rose-600">{formatCurrency(editingSale.total - editPaid)}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditSaleOpen(false)}>Anulo</Button>
            <Button
              onClick={async () => {
                try {
                  const newDebt = Math.max(0, editingSale.total - editPaid);
                  await api.put(`/sales/${editingSale.id}`, {
                    ...editingSale,
                    paid: editPaid,
                    debt: newDebt
                  });
                  toast.success('Shitja u përditësua');
                  setIsEditSaleOpen(false);
                  loadData();
                } catch (e) {
                  toast.error('Gabim në përditësim');
                }
              }}
            >
              Ruaj Ndryshimet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={isReceiptOpen} onOpenChange={setIsReceiptOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Shitja u Regjistrua</DialogTitle>
            <DialogDescription>Fatura #{lastSale?.id?.slice(0, 8)}</DialogDescription>
          </DialogHeader>
          {lastSale && (
            <div className="py-2 space-y-3">
              <div className="text-center">
                <p className="text-3xl font-bold font-mono text-primary">{formatCurrency(lastSale.total)}</p>
                <p className="text-slate-500">{lastSale.customer_name}</p>
              </div>

              {lastSale.change > 0 && (
                <div className="bg-lime-500 text-white p-4 rounded-lg text-center">
                  <p className="text-sm uppercase">Kusur</p>
                  <p className="text-3xl font-mono font-bold">{formatCurrency(lastSale.change)}</p>
                </div>
              )}

              {lastSale.debt > 0 && (
                <div className="bg-rose-500 text-white p-3 rounded-lg text-center">
                  <p className="text-sm">Borxh: <span className="font-mono font-bold">{formatCurrency(lastSale.debt)}</span></p>
                </div>
              )}

              {lastSale.warrantyCreated && (
                <div className="bg-violet-100 text-violet-800 p-3 rounded-lg text-center">
                  <Shield className="w-5 h-5 inline mr-1" />
                  Garanci {lastSale.warrantyMonths} muaj u krijua
                </div>
              )}

              {!lastSale.warrantyCreated && postSaleWarrantyData.customerId && supportsWarranties && (
                <div className="bg-violet-50 border border-violet-200 p-3 rounded-lg text-center">
                  <p className="text-sm text-violet-700 mb-2">Dëshironi të krijoni garanci?</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setIsReceiptOpen(false); setIsPostSaleWarrantyOpen(true); }}
                    className="border-violet-300 text-violet-700 hover:bg-violet-100"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Krijo Garanci
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex flex-col gap-2 sm:flex-col">
            <div className="flex gap-2 w-full">
              <Button variant="outline" onClick={() => setIsReceiptOpen(false)} className="flex-1">
                Mbyll
              </Button>
              <Button onClick={printReceipt} className="flex-1" data-testid="print-receipt-btn">
                <Printer className="w-4 h-4 mr-2" />
                Faturë
              </Button>
            </div>
            {lastSale?.warrantyCreated && (
              <Button variant="outline" onClick={printWarrantyCard} className="w-full border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="print-warranty-btn">
                <Shield className="w-4 h-4 mr-2" />
                Printo Kartë Garancie
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Customer Modal */}
      <Dialog open={isNewCustomerOpen} onOpenChange={setIsNewCustomerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Klient i Ri</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Emri *</Label>
              <Input
                value={newCustomerData.name}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                placeholder="Emri i klientit"
                autoFocus
              />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input
                value={newCustomerData.phone}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                placeholder="+355 6X XXX XXXX"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newCustomerData.email}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Adresa</Label>
              <Input
                value={newCustomerData.address}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, address: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewCustomerOpen(false)}>Anulo</Button>
            <Button onClick={createNewCustomer}>Krijo Klientin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-Sale Warranty Modal - Full Version */}
      <Dialog open={isPostSaleWarrantyOpen} onOpenChange={(open) => {
        if (!open) { setCreatedWarranty(null); }
        setIsPostSaleWarrantyOpen(open);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              {createdWarranty ? 'Garancia u Krijua' : 'Krijo Garanci pas Shitjes'}
            </DialogTitle>
          </DialogHeader>

          {!createdWarranty ? (
            <>
              <div className="space-y-4">
                {/* Customer Info */}
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm text-slate-600">Klienti:</p>
                  <p className="font-semibold">{postSaleWarrantyData.customerName}</p>
                </div>

                {/* Product Selection */}
                <div>
                  <Label>Produkti *</Label>
                  <Select
                    value={postWarrantyForm.product_id || ''}
                    onValueChange={(v) => {
                      const item = postSaleWarrantyData.items?.find(i => i.product_id === v);

                      // Auto-detect category
                      let detectedCategory = 'smartphone';
                      if (item?.product_name) {
                        const keywordMap = {
                          'tv': 'tv', 'televizor': 'tv',
                          'laptop': 'laptop', 'macbook': 'laptop', 'notebook': 'laptop',
                          'tablet': 'tablet', 'ipad': 'tablet',
                          'condicioner': 'ac', 'kondicioner': 'ac', 'ac': 'ac',
                          'lavatrice': 'appliance', 'frigorifer': 'appliance',
                          'aksesor': 'accessory', 'riparim': 'repair'
                        };
                        const lower = item.product_name.toLowerCase();
                        for (const [key, value] of Object.entries(keywordMap)) {
                          if (lower.includes(key)) {
                            detectedCategory = value;
                            break;
                          }
                        }
                      }

                      const catInfo = WARRANTY_CATEGORIES.find(c => c.value === detectedCategory);

                      setPostWarrantyForm({
                        ...postWarrantyForm,
                        product_id: v,
                        product_name: item?.product_name || '',
                        category: detectedCategory,
                        duration_months: catInfo?.defaultMonths || 12
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Zgjidh produktin" />
                    </SelectTrigger>
                    <SelectContent>
                      {postSaleWarrantyData.items?.map((item) => (
                        <SelectItem key={item.product_id} value={item.product_id}>
                          {item.product_name} (x{item.quantity})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Category */}
                  <div>
                    <Label>Kategoria</Label>
                    <Select
                      value={postWarrantyForm.category}
                      onValueChange={(v) => {
                        const cat = WARRANTY_CATEGORIES.find(c => c.value === v);
                        setPostWarrantyForm({
                          ...postWarrantyForm,
                          category: v,
                          duration_months: cat?.defaultMonths || 12
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WARRANTY_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Duration */}
                  <div>
                    <Label>Kohëzgjatja (muaj)</Label>
                    <NumericInput
                      value={postWarrantyForm.duration_months}
                      onChange={(e) => setPostWarrantyForm({ ...postWarrantyForm, duration_months: parseInt(e.target.value) || 0 })}
                      min={1}
                    />
                  </div>
                </div>

                {/* Serial/IMEI */}
                <div>
                  <Label>Serial/IMEI (Opsionale)</Label>
                  <Input
                    value={postWarrantyForm.serial_number}
                    onChange={(e) => setPostWarrantyForm({ ...postWarrantyForm, serial_number: e.target.value })}
                    placeholder="Shkruaj numrin serial ose IMEI"
                  />
                </div>

                {/* Notes */}
                <div>
                  <Label>Shënime (Opsionale)</Label>
                  <Input
                    value={postWarrantyForm.notes}
                    onChange={(e) => setPostWarrantyForm({ ...postWarrantyForm, notes: e.target.value })}
                    placeholder="Shënime shtesë..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsPostSaleWarrantyOpen(false)}>Anulo</Button>
                <Button
                  onClick={async () => {
                    if (!postWarrantyForm.product_id) {
                      toast.error('Zgjidh një produkt');
                      return;
                    }
                    try {
                      const startDate = new Date();
                      const endDate = new Date();
                      endDate.setMonth(endDate.getMonth() + postWarrantyForm.duration_months);

                      const warrantyData = {
                        type: 'product',
                        category: postWarrantyForm.category,
                        product_id: postWarrantyForm.product_id,
                        product_name: postWarrantyForm.product_name,
                        serial_number: postWarrantyForm.serial_number,
                        customer_id: postSaleWarrantyData.customerId,
                        customer_name: postSaleWarrantyData.customerName,
                        sale_id: postSaleWarrantyData.saleId,
                        start_date: startDate.toISOString(),
                        end_date: endDate.toISOString(),
                        duration_months: postWarrantyForm.duration_months,
                        notes: postWarrantyForm.notes || `Garanci nga shitja #${postSaleWarrantyData.saleId?.slice(0, 8)}`,
                        status: 'active'
                      };

                      const response = await api.post('/warranties', warrantyData);
                      toast.success('Garancia u krijua me sukses');

                      // Store created warranty for printing
                      setCreatedWarranty({
                        ...warrantyData,
                        id: response.data.id
                      });

                    } catch (error) {
                      toast.error('Gabim në krijimin e garancisë');
                    }
                  }}
                  disabled={!postWarrantyForm.product_id}
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Krijo Garancinë
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              {/* Success State with Print Option */}
              <div className="space-y-4">
                <div className="bg-lime-50 border border-lime-200 p-4 rounded-lg text-center">
                  <Shield className="w-12 h-12 text-lime-600 mx-auto mb-2" />
                  <p className="text-lime-800 font-semibold text-lg">Garancia u krijua me sukses!</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Produkti:</span>
                    <span className="font-medium">{createdWarranty.product_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Klienti:</span>
                    <span className="font-medium">{createdWarranty.customer_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Kategoria:</span>
                    <span className="font-medium">{WARRANTY_CATEGORIES.find(c => c.value === createdWarranty.category)?.label}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Kohëzgjatja:</span>
                    <span className="font-medium">{createdWarranty.duration_months} muaj</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Skadimi:</span>
                    <span className="font-medium">{formatDate(createdWarranty.end_date)}</span>
                  </div>
                  {createdWarranty.serial_number && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Serial/IMEI:</span>
                      <span className="font-medium font-mono">{createdWarranty.serial_number}</span>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="flex-col gap-2">
                <Button
                  onClick={() => {
                    // Print warranty card
                    const categoryLabel = WARRANTY_CATEGORIES.find(c => c.value === createdWarranty.category)?.label || createdWarranty.category;
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Garanci - ${createdWarranty.product_name}</title>
                        <style>
                          * { margin: 0; padding: 0; box-sizing: border-box; }
                          @page { size: A5; margin: 10mm; }
                          body { font-family: Arial, sans-serif; padding: 15px; max-width: 600px; margin: 0 auto; font-size: 11px; line-height: 1.4; }
                          .header { text-align: center; padding-bottom: 10px; margin-bottom: 15px; border-bottom: 2px solid #E52B50; }
                          .logo { width: 60px; height: 60px; background: #E52B50; border-radius: 8px; margin: 0 auto 8px; display: flex; align-items: center; justify-content: center; }
                          .logo-text { color: white; font-weight: bold; font-size: 10px; }
                          .header h1 { color: #E52B50; font-size: 20px; margin-bottom: 2px; }
                          .header h2 { color: #333; font-size: 14px; font-weight: normal; }
                          .info-section { display: grid; grid-template-columns: 100px 1fr; gap: 8px; margin-bottom: 15px; padding: 10px; background: #f9f9f9; border-radius: 5px; }
                          .info-label { color: #E52B50; font-weight: bold; font-size: 10px; }
                          .info-value { font-size: 11px; border-bottom: 1px dotted #ccc; padding-bottom: 2px; }
                          .warranty-period { background: #E52B50; color: white; text-align: center; padding: 10px; border-radius: 5px; margin-bottom: 15px; }
                          .warranty-period h3 { font-size: 16px; margin-bottom: 5px; }
                          .warranty-period p { font-size: 12px; }
                          .terms-section { font-size: 9px; line-height: 1.5; color: #333; }
                          .terms-section p { margin-bottom: 5px; }
                          .terms-section ol { margin-left: 15px; margin-bottom: 10px; }
                          .terms-section li { margin-bottom: 3px; }
                          .terms-section strong { color: #E52B50; }
                          .footer { margin-top: 15px; padding-top: 10px; border-top: 1px solid #ddd; display: flex; justify-content: space-between; }
                          .signature { width: 45%; text-align: center; }
                          .signature-line { border-top: 1px solid #333; margin-top: 30px; padding-top: 5px; font-size: 10px; }
                          @media print { body { padding: 0; } }
                        </style>
                      </head>
                      <body>
                        <div class="header">
                          <div class="logo"><span class="logo-text">MIRI<br>SHOP</span></div>
                          <h1>MIRI SHOP</h1>
                          <h2>${categoryLabel}</h2>
                        </div>
                        <div class="info-section">
                          <span class="info-label">Klienti:</span>
                          <span class="info-value">${createdWarranty.customer_name}</span>
                          <span class="info-label">Data:</span>
                          <span class="info-value">${formatDate(createdWarranty.start_date)}</span>
                          <span class="info-label">Modeli:</span>
                          <span class="info-value">${createdWarranty.product_name}</span>
                          <span class="info-label">Serial/IMEI:</span>
                          <span class="info-value">${createdWarranty.serial_number || '-'}</span>
                        </div>
                        <div class="warranty-period">
                          <h3>GARANCI ${createdWarranty.duration_months} MUAJ</h3>
                          <p>${formatDate(createdWarranty.start_date)} - ${formatDate(createdWarranty.end_date)}</p>
                        </div>
                        <div class="terms-section">
                          <p><strong>Garancia mbulohet kur:</strong></p>
                          <ol>
                            <li>Klienti i prezanton shitesit defektin brenda afatit kohor të përcaktuar.</li>
                            <li>Defekti eshte rezultat i problemeve te prodhimit.</li>
                          </ol>
                          <p><strong>GARANCIA NUK MBULON:</strong></p>
                          <ol>
                            <li>Dëmtime fizike, nga uji ose përdorim jo të duhur.</li>
                            <li>Vetë-riparimi ose riparimi nga persona jo të autorizuar.</li>
                          </ol>
                        </div>
                        <div class="footer">
                          <div class="signature"><div class="signature-line">Klienti</div></div>
                          <div class="signature"><div class="signature-line">Shitësi</div></div>
                        </div>
                        <script>window.onload = function() { setTimeout(function() { window.print(); }, 300); }</script>
                      </body>
                      </html>
                    `);
                    printWindow.document.close();
                  }}
                  className="w-full"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Printo Kartë Garancie
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCreatedWarranty(null);
                    setIsPostSaleWarrantyOpen(false);
                    setPostWarrantyForm({
                      type: 'product',
                      category: 'smartphone',
                      product_id: '',
                      product_name: '',
                      serial_number: '',
                      duration_months: 12,
                      notes: '',
                    });
                  }}
                  className="w-full"
                >
                  Mbyll
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Keyboard Shortcuts Help Modal */}
      <Dialog open={isHelpOpen} onOpenChange={setIsHelpOpen}>
        <DialogContent className="max-w-2xl bg-white border-0 rounded-3xl p-0 overflow-hidden shadow-2xl">
          <div className="bg-white p-6 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-2.5 rounded-xl">
                <Keyboard className="w-6 h-6 text-slate-700" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-slate-900">Shkurtoret e Tastierës</DialogTitle>
                <DialogDescription className="text-slate-500 font-medium mt-0.5">
                  Përdorni këto shkurtore për veprime të shpejta
                </DialogDescription>
              </div>
            </div>
          </div>

          <div className="p-6 bg-slate-50/50">
            <div className="grid grid-cols-2 gap-4">
              {KEYBOARD_SHORTCUTS.map((shortcut, idx) => (
                <div key={idx} className="bg-white p-3 rounded-xl border border-slate-200/60 shadow-sm flex items-center gap-4 group hover:border-primary/20 hover:shadow-md transition-all">
                  <span className="flex items-center justify-center w-12 h-9 bg-slate-100 font-mono font-bold text-slate-700 rounded-lg text-sm border-b-2 border-slate-200 group-hover:bg-primary group-hover:text-white group-hover:border-primary/50 transition-colors">
                    {shortcut.key}
                  </span>
                  <span className="font-semibold text-slate-600 group-hover:text-slate-900">
                    {shortcut.action}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-4 border-t border-slate-100 flex justify-end">
            <Button onClick={() => setIsHelpOpen(false)} variant="outline" className="px-8 font-bold rounded-xl h-11 border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900">
              Mbyll
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div >
  );
}
